import { FormlyFieldConfig } from '@ngx-formly/core';

import { IdentityFormat, businessTaxIdFormat, socialIdFormat } from '@shared/constants/identity-formats';

/**
 * Formly expressions that re-derive a national-id field's shape from its sibling country select.
 *
 * These have to be expressions rather than static props because the user can change the country after the field
 * has rendered - a static `pattern` would keep validating a Canadian SIN against the US rules. Formly re-reads
 * `props.pattern` on every validation run and calls `updateValidity` when an observed prop changes, so swapping the
 * pattern this way really does re-validate.
 *
 * Shared because the same field is declared five times across the portal (customer create, the plain and secure
 * variants on customer update, and the same pair on the beneficiary form). Diverging copies is exactly how the
 * mask-length bug this ticket fixes got in.
 */
const formatExpressions = (lookup: (countryCode?: string | null) => IdentityFormat, countryCodeKey: string) => ({
  'props.label': (field: FormlyFieldConfig) => lookup(field.model?.[countryCodeKey]).label,
  'props.pattern': (field: FormlyFieldConfig) => lookup(field.model?.[countryCodeKey]).pattern,
  'props.maxLength': (field: FormlyFieldConfig) => lookup(field.model?.[countryCodeKey]).maxLength,
  'props.maskString': (field: FormlyFieldConfig) => lookup(field.model?.[countryCodeKey]).mask,
  'props.placeholder': (field: FormlyFieldConfig) => lookup(field.model?.[countryCodeKey]).mask,
  'props.securedValuePrefix': (field: FormlyFieldConfig) => lookup(field.model?.[countryCodeKey]).securedValuePrefix,
});

export const socialIdExpressions = (countryCodeKey = 'socialIdCountryCode') => formatExpressions(socialIdFormat, countryCodeKey);

export const businessTaxIdExpressions = (countryCodeKey = 'taxIdCountryCode') => formatExpressions(businessTaxIdFormat, countryCodeKey);

/**
 * Pattern-error message factories.
 *
 * Parameterised on the same key as the expressions above, so a form that renames its country control cannot end up
 * with a Canadian mask and a "valid SSN" error message.
 */
export const socialIdPatternMessage =
  (countryCodeKey = 'socialIdCountryCode') =>
  (_error: unknown, field: FormlyFieldConfig) =>
    socialIdFormat(field.model?.[countryCodeKey]).patternMessage;

export const businessTaxIdPatternMessage =
  (countryCodeKey = 'taxIdCountryCode') =>
  (_error: unknown, field: FormlyFieldConfig) =>
    businessTaxIdFormat(field.model?.[countryCodeKey]).patternMessage;
